'use client'

import { useEffect, useRef, useState } from 'react'

import { Button } from '@/core/components/form/button/button'
import { cn } from '@/core/utils/common'
import { Textarea } from '@/core/components/form/textarea'

import { COMMENT_MAX } from '@/features/veluvit/components/comments/use-work-comments'
import { useAppData } from '@/features/veluvit/hooks/use-app-data'

import type { ReactNode } from 'react'

/** 내 머리글자 원 — 댓글 줄의 작성자 원과 같은 모양 */
function MeAvatar({ small }: { small?: boolean }) {
  const { data } = useAppData()
  const name = data?.user && !data.user.guest ? data.user.name : ''
  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid flex-none place-items-center rounded-full bg-point/25 font-bold text-point',
        small ? 'size-6 text-c11' : 'size-8 text-c13'
      )}
    >
      {Array.from(name || '나')[0]}
    </span>
  )
}

/**
 * 댓글 입력 한 줄 — 유튜브 댓글과 같은 동작(2026-10-08):
 * 평소엔 밑줄 한 줄(`댓글 추가…`), 누르면(또는 글이 있으면) 아래에 `취소` · `등록` 이 나온다. 취소는 글을 비우고 접는다.
 * 답글 입력은 alwaysOpen 으로 처음부터 버튼을 보이고, initial(`@이름 `)을 미리 넣고 커서를 끝에 둔다.
 * 글자 수만큼 높이가 늘어난다(field-sizing). 모바일 16px — iOS 가 입력칸을 눌렀을 때 화면을 확대하지 않게.
 */
export default function CommentInput({
  placeholder,
  submitLabel,
  onSubmit,
  onCancel,
  initial = '',
  autoFocus,
  alwaysOpen,
  small,
  extra,
  below,
  className,
}: {
  placeholder: string
  submitLabel: string
  /** 보내기 — true 면 비우고 접는다 */
  onSubmit: (text: string) => Promise<boolean>
  onCancel?: () => void
  initial?: string
  autoFocus?: boolean
  alwaysOpen?: boolean
  /** 답글 입력(작은 원) */
  small?: boolean
  /** 버튼 줄 왼쪽에 붙는 것(상세의 스냅 붙이기) */
  extra?: ReactNode
  /** 입력칸 바로 아래(붙인 스냅 미리보기 등) */
  below?: ReactNode
  className?: string
}) {
  const [text, setText] = useState(initial)
  const [focused, setFocused] = useState(false)
  const [pending, setPending] = useState(false)
  const ref = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (!autoFocus) return
    const el = ref.current
    if (!el) return
    el.focus()
    el.setSelectionRange(el.value.length, el.value.length)
  }, [autoFocus])

  const open = alwaysOpen || focused || text.length > 0
  const empty = !text.trim() || text.trim() === initial.trim()

  const close = () => {
    setText('')
    setFocused(false)
    ref.current?.blur()
    onCancel?.()
  }
  const submit = async () => {
    if (empty || pending) return
    setPending(true)
    const ok = await onSubmit(text)
    setPending(false)
    if (ok) close()
  }

  return (
    <div className={cn('flex gap-3', className)}>
      <MeAvatar small={small} />
      <div className="min-w-0 flex-1">
        <Textarea
          variant="unstyled"
          ref={ref}
          rows={1}
          value={text}
          maxLength={COMMENT_MAX}
          placeholder={placeholder}
          aria-label={placeholder}
          onFocus={() => setFocused(true)}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // Ctrl/⌘ + Enter 로 보내기 — Enter 는 줄바꿈
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault()
              void submit()
            }
          }}
          className="block max-h-40 w-full resize-none border-b border-foreground/20 bg-transparent pt-1 pb-1.5 text-c14 leading-[1.55] text-t1 outline-none [field-sizing:content] placeholder:text-t3 transition-colors focus:border-foreground max-over6:text-c16"
        />
        {below}
        {open && (
          <div className="mt-2 flex items-center gap-1.5">
            {extra}
            <span className="ml-auto" />
            <Button variant="ghost" size="sm" shape="pill" onClick={close}>
              취소
            </Button>
            <Button
              variant="primary"
              size="sm"
              shape="pill"
              bold
              disabled={pending || empty}
              onClick={() => void submit()}
            >
              {submitLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
